import {
  collection,
  doc,
  DocumentSnapshot,
  getCountFromServer,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  QueryConstraint,
  setDoc,
  startAfter,
  where,
} from "firebase/firestore";
import { db } from "../utils/init-firebase";
import {
  LEADERBOARD_COLLECTION_NAME,
  LeaderboardData,
  UserData,
  TimeFilterNameToUseForLeaderboard,
  MIN_NUM_OF_PEOPLE_TO_SHOW_UNIVERSITY_FILTER,
  LeaderboardLeagueGroup,
  LEADERBOARD_LEAGUE_GROUPS_COLLECTION_NAME,
  LeaderboardPositionZone,
  LeaderboardLeagues,
  JUNGLE_CLOUDFLARE_BASE_URL,
} from "../../constants/GlobalTypesAndEnumsAndConstants";
import { getCurrentWeekKeyNameIdentifier, getCurrentMonthKeyNameIdentifier, trackEvent } from "../utils/helper-methods";
import {
  generateWeekIdentifierBasedOnMondayStartInPacificTime,
  LeaderboardLeaguesConfig,
} from "../utils/gamification-helper-methods/progression-and-river-animation";
import { updateUser } from "./UserModel";
import { cardStore } from "../../zustand/globalStore";

export const updateLeaderboardScoreForUser = async (user: UserData, xpScoreEarnedForAnswer?: number) => {
  const newXpScoreToAdd =
    typeof xpScoreEarnedForAnswer === "number"
      ? xpScoreEarnedForAnswer
      : cardStore.getState().xpScoreToAddForCurrentCard || 0;
  if (!user || newXpScoreToAdd <= 0) {
    return;
  }
  // Check if leaderboard data exists for the user
  // const leaderboardData = await getLeaderboardDataForUserId(user?.id);

  if (user?.gamification_properties?.all_time_xp_earned <= 0 && newXpScoreToAdd <= 0) {
    return;
  }
  if (newXpScoreToAdd <= 0) {
    return;
  }
  await fetch(`${JUNGLE_CLOUDFLARE_BASE_URL}/leaderboard/update_leaderboard_score_for_user`, {
    method: "POST",
    body: JSON.stringify({
      user_id: user?.id,
      new_xp_score_to_add: newXpScoreToAdd,
    }),
    headers: {
      "Content-Type": "application/json",
    },
  })
    .then(async (response) => {
      return response.json();
    })
    .then(async (response) => {
      if (response.status !== "success") {
        trackEvent("Error: Failed to update leaderboard data for user", { userId: user?.id });
      }
    })
    .catch((error) => {
      trackEvent(
        "Error: Failed to update leaderboard data for user",
        {
          errorMessage: error.message,
        },
        user
      );
    });
};

export const updateLeaderboardForUser = async (userId: string, dataToUpdate: any) => {
  if (!userId) {
    return;
  }

  try {
    const leaderboardUserDocRef = doc(db, LEADERBOARD_COLLECTION_NAME, userId);
    await setDoc(
      leaderboardUserDocRef,
      {
        ...dataToUpdate,
      },
      { merge: true }
    );
  } catch (error) {
    // console.error("Error updating leaderboard for user", error);
  }
};

export const getLeaderboardDataForUserId = async (
  userId: string,
  returnUserLeaderboardRanking: boolean = false, // if true, we calculate user's leaderboard ranking and return it
  shouldFilterForUsersUniversityDataToGetTheirRanking: boolean = false,
  timeFilterNameToUseForLeaderboard?: TimeFilterNameToUseForLeaderboard
): Promise<LeaderboardData | LeaderboardData | null> => {
  try {
    const leaderboardUserDocRef = doc(db, LEADERBOARD_COLLECTION_NAME, userId);
    const leaderboardDoc = await getDoc(leaderboardUserDocRef);

    if (leaderboardDoc.exists()) {
      const userLeaderboardData = leaderboardDoc.data();

      if (!returnUserLeaderboardRanking) {
        return userLeaderboardData as LeaderboardData;
      }

      const currentWeekKeyNameIdentifier = getCurrentWeekKeyNameIdentifier();
      const currentMonthKeyNameIdentifier = getCurrentMonthKeyNameIdentifier();
      // Get user's XP score and university name
      const userAllTimeXpEarned = userLeaderboardData.all_time_xp_earned;
      const userUniversityName = userLeaderboardData.university_name;

      // if the user hasn't answered any question in the current week, then their current_week_xp_earned will be 0
      let userCurrentWeekXpEarned = 0;
      if (userLeaderboardData.last_updated_week_key_name === currentWeekKeyNameIdentifier) {
        userCurrentWeekXpEarned = userLeaderboardData.current_week_xp_earned;
      }

      let userCurrentMonthXpEarned = 0;
      if (userLeaderboardData.last_updated_month_key_name === currentMonthKeyNameIdentifier) {
        userCurrentMonthXpEarned = userLeaderboardData.current_month_xp_earned;
      }

      // Query to get all users with higher XP than the current user
      // This helps determine the user's ranking in the leaderboard
      // Create base query conditions
      const userleaderboardQueryConstraints: QueryConstraint[] = [];

      // Add time-based conditions
      if (
        timeFilterNameToUseForLeaderboard === TimeFilterNameToUseForLeaderboard.LEADERBOARD_CURRENT_WEEK_FILTER_NAME
      ) {
        userleaderboardQueryConstraints.push(
          where("current_week_xp_earned", ">", userCurrentWeekXpEarned),
          where("last_updated_week_key_name", "==", currentWeekKeyNameIdentifier)
        );
      } else if (
        timeFilterNameToUseForLeaderboard === TimeFilterNameToUseForLeaderboard.LEADERBOARD_CURRENT_MONTH_FILTER_NAME
      ) {
        userleaderboardQueryConstraints.push(
          where("current_month_xp_earned", ">", userCurrentMonthXpEarned),
          where("last_updated_month_key_name", "==", currentMonthKeyNameIdentifier)
        );
      } else {
        userleaderboardQueryConstraints.push(where("all_time_xp_earned", ">", userAllTimeXpEarned));
      }

      // Add university filter if required
      if (shouldFilterForUsersUniversityDataToGetTheirRanking && userUniversityName) {
        userleaderboardQueryConstraints.push(where("university_name", "==", userUniversityName));
      }

      let getAllUsersWithMoreXpThanCurrentUserForTimeFilterQuery = query(
        collection(db, LEADERBOARD_COLLECTION_NAME),
        ...userleaderboardQueryConstraints
      );

      // Execute the count aggregation query
      // getCountFromServer retrieve the count of documents matching a specific query without downloading all the documents themselves.
      let userAllTimeOrCurrentWeekXpRanking;
      const usersWithMoreAllTimeXPThanCurrentUserCountSnapShot = await getCountFromServer(
        getAllUsersWithMoreXpThanCurrentUserForTimeFilterQuery
      );
      const usersWithMoreAllTimeXPThanCurrentUserDoc = usersWithMoreAllTimeXPThanCurrentUserCountSnapShot.data().count;
      userAllTimeOrCurrentWeekXpRanking = usersWithMoreAllTimeXPThanCurrentUserDoc + 1;

      return {
        user_id: userId,
        character_with_accessory_selected: userLeaderboardData.character_with_accessory_selected,
        character_team_selected: userLeaderboardData.character_team_selected,
        all_time_xp_earned: userLeaderboardData.all_time_xp_earned,
        current_week_xp_earned: userCurrentWeekXpEarned,
        current_month_xp_earned: userCurrentMonthXpEarned,
        university_name: userUniversityName,
        user_name: userLeaderboardData.user_name,
        last_updated_week_key_name: userLeaderboardData.last_updated_week_key_name,
        ranking: userAllTimeOrCurrentWeekXpRanking,
      } as LeaderboardData;
    } else {
      return null;
    }
  } catch (error) {
    return null;
  }
};

export const getLeaderboardLeagueGroupDataById = async (groupId: string): Promise<LeaderboardLeagueGroup | null> => {
  try {
    const leaderboardLeagueGroupDocRef = doc(db, LEADERBOARD_LEAGUE_GROUPS_COLLECTION_NAME, groupId);
    const leaderboardLeagueGroupSnapshot = await getDoc(leaderboardLeagueGroupDocRef);
    if (!leaderboardLeagueGroupSnapshot.exists()) {
      return null;
    }
    return leaderboardLeagueGroupSnapshot.data() as LeaderboardLeagueGroup;
  } catch (error) {
    console.error("error getting leaderboard league group data by id", error);
    return null;
  }
};

export const getPaginatedLeaderboardData = async (
  prevBatchLastLeaderboardEntryDoc: DocumentSnapshot | null,
  batchSize: number,
  timeFilterNameToUseForLeaderboard: TimeFilterNameToUseForLeaderboard,
  universityName?: string
): Promise<{ leaderboardDataArray: LeaderboardData[]; currBatchLastLeaderboardEntryDoc: DocumentSnapshot | null }> => {
  try {
    const leaderboardCollectionRef = collection(db, LEADERBOARD_COLLECTION_NAME);

    // Create query constraints:
    // 1. Order by all_time_xp_earned in descending order
    // 2. Filter by university_name if provided
    // 3. Start after the last document for pagination
    // 4. Limit the number of results to batchSize

    let paginatedleaderboardQueryConstraints: QueryConstraint[] = [];
    if (timeFilterNameToUseForLeaderboard === TimeFilterNameToUseForLeaderboard.LEADERBOARD_CURRENT_WEEK_FILTER_NAME) {
      const currentWeekKeyNameIdentifier = getCurrentWeekKeyNameIdentifier();
      paginatedleaderboardQueryConstraints = [
        orderBy("current_week_xp_earned", "desc"),
        where("last_updated_week_key_name", "==", currentWeekKeyNameIdentifier),
      ];
    } else if (
      timeFilterNameToUseForLeaderboard === TimeFilterNameToUseForLeaderboard.LEADERBOARD_CURRENT_MONTH_FILTER_NAME
    ) {
      const currentMonthKeyNameIdentifier = getCurrentMonthKeyNameIdentifier();
      paginatedleaderboardQueryConstraints = [
        orderBy("current_month_xp_earned", "desc"),
        where("last_updated_month_key_name", "==", currentMonthKeyNameIdentifier),
      ];
    } else {
      paginatedleaderboardQueryConstraints = [orderBy("all_time_xp_earned", "desc")];
    }

    if (universityName) {
      paginatedleaderboardQueryConstraints.push(where("university_name", "==", universityName));
    }
    if (prevBatchLastLeaderboardEntryDoc) {
      paginatedleaderboardQueryConstraints.push(startAfter(prevBatchLastLeaderboardEntryDoc));
    }
    paginatedleaderboardQueryConstraints.push(limit(batchSize));

    const leaderboardDocQuery = query(leaderboardCollectionRef, ...paginatedleaderboardQueryConstraints);

    const leaderboardDoc = await getDocs(leaderboardDocQuery);
    if (leaderboardDoc.empty) {
      return { leaderboardDataArray: [], currBatchLastLeaderboardEntryDoc: null };
    }

    const leaderboardDataArray: LeaderboardData[] = leaderboardDoc.docs.map((doc) => {
      return doc.data() as LeaderboardData;
    });

    const currBatchLastLeaderboardEntryDoc = leaderboardDoc.docs[leaderboardDoc.docs.length - 1] || null;

    return { leaderboardDataArray, currBatchLastLeaderboardEntryDoc };
  } catch (error) {
    console.error("error", error);
    return { leaderboardDataArray: [], currBatchLastLeaderboardEntryDoc: null };
  }
};

export const getLeaderboardDataAroundUser = async (
  userId: string,
  batchSize: number,
  timeFilterNameToUseForLeaderboard: TimeFilterNameToUseForLeaderboard,
  universityName?: string
): Promise<LeaderboardData[]> => {
  try {
    const userLeaderboardData = (await getLeaderboardDataForUserId(
      userId,
      true,
      !!universityName,
      timeFilterNameToUseForLeaderboard
    )) as LeaderboardData;

    if (!userLeaderboardData) {
      return [];
    }

    const leaderboardCollectionRef = collection(db, LEADERBOARD_COLLECTION_NAME);
    let xpField: string;
    let userXp: number;
    let timeConstraint: QueryConstraint | undefined;

    if (timeFilterNameToUseForLeaderboard === TimeFilterNameToUseForLeaderboard.LEADERBOARD_CURRENT_WEEK_FILTER_NAME) {
      xpField = "current_week_xp_earned";
      userXp = userLeaderboardData.current_week_xp_earned;
      timeConstraint = where("last_updated_week_key_name", "==", getCurrentWeekKeyNameIdentifier());
    } else if (
      timeFilterNameToUseForLeaderboard === TimeFilterNameToUseForLeaderboard.LEADERBOARD_CURRENT_MONTH_FILTER_NAME
    ) {
      xpField = "current_month_xp_earned";
      userXp = userLeaderboardData.current_month_xp_earned;
      timeConstraint = where("last_updated_month_key_name", "==", getCurrentMonthKeyNameIdentifier());
    } else {
      xpField = "all_time_xp_earned";
      userXp = userLeaderboardData.all_time_xp_earned;
    }

    const queryConstraints: QueryConstraint[] = [where(xpField, ">=", 0)];
    if (universityName) {
      queryConstraints.push(where("university_name", "==", universityName));
    }
    if (timeConstraint) {
      queryConstraints.push(timeConstraint);
    }

    // Query for users with more XP (ranked higher) - limit to 100 above
    const higherRankedQuery = query(
      leaderboardCollectionRef,
      ...queryConstraints,
      where(xpField, ">", userXp),
      orderBy(xpField, "asc"),
      limit(100)
    );

    // Query for users with less or equal XP (ranked lower) - limit to 100 below
    const lowerRankedQuery = query(
      leaderboardCollectionRef,
      ...queryConstraints,
      where(xpField, "<=", userXp),
      orderBy(xpField, "desc"),
      limit(100)
    );

    const [higherRankedSnapshot, lowerRankedSnapshot] = await Promise.all([
      getDocs(higherRankedQuery),
      getDocs(lowerRankedQuery),
    ]);

    const higherRankedData = higherRankedSnapshot.docs.map((doc) => doc.data() as LeaderboardData).reverse();
    const lowerRankedData = lowerRankedSnapshot.docs.map((doc) => doc.data() as LeaderboardData);

    // Ensure the current user is included
    const currentUserData = {
      user_id: userLeaderboardData.user_id,
      character_with_accessory_selected: userLeaderboardData.character_with_accessory_selected,
      character_team_selected: userLeaderboardData.character_team_selected,
      all_time_xp_earned: userLeaderboardData.all_time_xp_earned,
      current_week_xp_earned: userLeaderboardData.current_week_xp_earned,
      current_month_xp_earned: userLeaderboardData.current_month_xp_earned,
      university_name: userLeaderboardData.university_name,
      user_name: userLeaderboardData.user_name,
    } as LeaderboardData;

    // Combine data and ensure current user is included
    const allData = [
      ...higherRankedData,
      currentUserData,
      ...lowerRankedData.filter((data) => data.user_id !== userId),
    ];

    const combinedData = allData.filter(
      (value, index, self) => index === self.findIndex((t) => t.user_id === value.user_id) // ensure unique users
    );

    const userRank = userLeaderboardData.ranking;
    const numAbove = higherRankedData.length;

    return combinedData.map((data, index) => {
      const ranking = userRank - numAbove + index;
      return {
        user_id: data.user_id,
        character_with_accessory_selected: data.character_with_accessory_selected,
        character_team_selected: data.character_team_selected,
        all_time_xp_earned: data.all_time_xp_earned,
        current_week_xp_earned: data.current_week_xp_earned,
        current_month_xp_earned: data.current_month_xp_earned,
        university_name: data.university_name,
        user_name: data.user_name,
        ranking: ranking,
      } as LeaderboardData;
    });
  } catch (error) {
    console.error("error getting leaderboard data around user", error);
    return [];
  }
};

export const didMeetMinimumNumberOfUsersAtUniversityToShowLeaderboard = async (
  universityName: string
): Promise<boolean> => {
  try {
    const leaderboardCollectionRef = collection(db, LEADERBOARD_COLLECTION_NAME);
    const universityMemberCountQuery = query(
      leaderboardCollectionRef,
      where("university_name", "==", universityName),
      limit(MIN_NUM_OF_PEOPLE_TO_SHOW_UNIVERSITY_FILTER + 1) // We only need to check if there are more than MIN_NUM_OF_PEOPLE_TO_SHOW_UNIVERSITY_FILTER, so we limit to MIN_NUM_OF_PEOPLE_TO_SHOW_UNIVERSITY_FILTER+1
    );

    const universityMemberCountSnapshot = await getCountFromServer(universityMemberCountQuery);
    const universityMemberCount = universityMemberCountSnapshot.data().count;
    return universityMemberCount >= MIN_NUM_OF_PEOPLE_TO_SHOW_UNIVERSITY_FILTER;
  } catch (error) {
    console.error("Error checking university member count:", error);
    return false;
  }
};

export const getLeaderboardGroupDataForUserId = async (
  user: UserData = null,
  shouldShowDataForLastWeek: boolean = false
): Promise<LeaderboardLeagueGroup | null> => {
  try {
    const currentGroupId = shouldShowDataForLastWeek
      ? user?.gamification_properties?.leaderboard_leagues_metadata?.last_group_id_user_participated_in
      : user?.gamification_properties?.leaderboard_leagues_metadata?.current_group_id;

    if (!currentGroupId) {
      return null;
    }

    const leaderboardLeagueGroupDocRef = doc(db, LEADERBOARD_LEAGUE_GROUPS_COLLECTION_NAME, currentGroupId);
    const leaderboardLeagueGroupSnapshot = await getDoc(leaderboardLeagueGroupDocRef);

    if (!leaderboardLeagueGroupSnapshot.exists()) {
      return null;
    }

    const leaderboardLeagueGroup = leaderboardLeagueGroupSnapshot.data() as LeaderboardLeagueGroup;
    let week_id = leaderboardLeagueGroup.week_id;
    const lastWeekId = generateWeekIdentifierBasedOnMondayStartInPacificTime(new Date(), -1);
    if (week_id !== lastWeekId && shouldShowDataForLastWeek) {
      let group_leagues_participated_in =
        user?.gamification_properties?.leaderboard_leagues_metadata?.group_leagues_participated_in;
      for (const group_league_participated_in of group_leagues_participated_in) {
        let penultimateLeaderboardLeagueGroupDocRef = doc(
          db,
          LEADERBOARD_LEAGUE_GROUPS_COLLECTION_NAME,
          group_league_participated_in
        );
        let penultimateLeaderboardLeagueGroupSnapshot = await getDoc(penultimateLeaderboardLeagueGroupDocRef);
        const penultimateLeaderboardLeagueGroup =
          penultimateLeaderboardLeagueGroupSnapshot.data() as LeaderboardLeagueGroup;
        let currentUserLeagueName =
          user?.gamification_properties?.leaderboard_leagues_metadata?.current_league_rank_name;
        if (penultimateLeaderboardLeagueGroup.week_id === lastWeekId) {
          if (
            user?.gamification_properties?.leaderboard_leagues_metadata?.current_league_rank_name ===
            penultimateLeaderboardLeagueGroup.league_name
          ) {
            const userInGroup = penultimateLeaderboardLeagueGroup.users_in_group.find(
              (userInGroup) => userInGroup.user_id === user?.id
            );
            let currentLeaderboardLeagueUserPosition =
              LeaderboardLeaguesConfig[penultimateLeaderboardLeagueGroup.league_name as LeaderboardLeagues];

            if (userInGroup?.zone_position === LeaderboardPositionZone.PROMOTE) {
              const nextLeaderboardLeagueGroupConfig =
                Object.values(LeaderboardLeaguesConfig)
                  .filter((leagueConfig) => leagueConfig.leagueTier > currentLeaderboardLeagueUserPosition.leagueTier)
                  .sort((leagueA, leagueB) => leagueA.leagueTier - leagueB.leagueTier)[0] ?? null;
              currentUserLeagueName = nextLeaderboardLeagueGroupConfig?.league_name;
            }
            if (userInGroup?.zone_position === LeaderboardPositionZone.DEMOTE) {
              const previousLeaderboardLeagueGroupConfig =
                Object.values(LeaderboardLeaguesConfig)
                  .filter((leagueConfig) => leagueConfig.leagueTier < 3)
                  .sort((leagueA, leagueB) => leagueB.leagueTier - leagueA.leagueTier)[0] ?? null;
              currentUserLeagueName = previousLeaderboardLeagueGroupConfig?.league_name;
            }
          }
          setTimeout(async () => {
            updateUser(user?.id, {
              gamification_properties: {
                leaderboard_leagues_metadata: {
                  ...user?.gamification_properties?.leaderboard_leagues_metadata,
                  last_group_id_user_participated_in: group_league_participated_in,
                  current_league_rank_name: currentUserLeagueName,
                },
              },
            });
          }, 3000);
          return penultimateLeaderboardLeagueGroupSnapshot.data() as LeaderboardLeagueGroup;
        }
      }
    }
    return leaderboardLeagueGroup;
  } catch (error) {
    console.error("Error getting leaderboard group data for user", error);
    return null;
  }
};

export const updateLeaderboardGroupData = async (groupId: string, leaderboardGroupData: LeaderboardLeagueGroup) => {
  try {
    const leaderboardLeagueGroupDocRef = doc(db, LEADERBOARD_LEAGUE_GROUPS_COLLECTION_NAME, groupId);
    await setDoc(leaderboardLeagueGroupDocRef, leaderboardGroupData);
  } catch (error) {
    console.error("Error updating leaderboard group data", error);
  }
};
